import { FunctionComponent } from 'react'
import { TableSortLabel } from '@mui/material'
import {
  SiteRFPSummary,
  SiteRFPSummariesSortByParam,
} from '@black-bear-energy/black-bear-energy-common'
import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  rucksack,
} from '@black-bear-energy/rucksack'
import houseSearch from '../../assets/house-search.svg'
import { SITES_COLUMNS } from '../helpers/Sites/tableConstants'
import { SitesTableSection } from './SitesTableSection'

const defaultTableHeight = '45.25rem'

export const SitesTable: FunctionComponent<SitesTableProps> = ({
  records,
  isPending,
  isError,
  sortBy,
  setSortBy,
  sortAsc,
  setSortAsc,
  search,
  hasActiveFilters,
}) => {
  return (
    <Box sx={{ overflowX: 'auto', marginTop: 2 }}>
      {isPending ? (
        <Skeleton variant="rounded" height={defaultTableHeight} />
      ) : (
        <Box sx={{ minHeight: defaultTableHeight }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', minWidth: '800px' }}>
              <TableHead sx={{ '& .MuiTableCell-head': { fontSize: '14px' } }}>
                <TableRow>
                  {SITES_COLUMNS.map((column) => {
                    const sortKey = column.sortKey
                    return (
                      <TableCell
                        key={column.title}
                        sx={{
                          width: `${column.width}%`,
                          backgroundColor: rucksack.palette.background.card,
                        }}
                      >
                        {sortKey ? (
                          <TableSortLabel
                            active={sortBy === sortKey}
                            direction={
                              sortBy === sortKey
                                ? sortAsc
                                  ? 'asc'
                                  : 'desc'
                                : 'asc'
                            }
                            onClick={() => {
                              if (sortBy === sortKey) {
                                setSortAsc(!sortAsc)
                              } else {
                                setSortBy(sortKey)
                                setSortAsc(true)
                              }
                            }}
                          >
                            {column.title}
                          </TableSortLabel>
                        ) : (
                          column.title
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 && !isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={SITES_COLUMNS.length}
                      sx={{ border: 0 }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2.5,
                          py: 5,
                        }}
                      >
                        <img src={houseSearch} alt="" />
                        <Typography
                          variant="body1"
                          sx={{ textAlign: 'center' }}
                        >
                          {hasActiveFilters ? (
                            <>
                              No results found.
                              <br />
                              Please try a new search.
                            </>
                          ) : (
                            <>
                              Your sites will appear here once an RFP has been
                              released.
                              <br />
                              Contact your Black Bear Energy driver if you have
                              any questions.
                            </>
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record) => (
                    <SitesTableSection
                      key={record.siteRFPId}
                      record={record}
                      search={search}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  )
}

SitesTable.displayName = 'SitesTable'

export interface SitesTableProps {
  records: SiteRFPSummary[]
  isPending: boolean
  isError?: boolean
  sortBy: SiteRFPSummariesSortByParam
  setSortBy: (newSortBy: SiteRFPSummariesSortByParam) => void
  sortAsc: boolean
  setSortAsc: (newSortAsc: boolean) => void
  search?: string
  hasActiveFilters: boolean
}
